
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NavigateFunction } from "react-router-dom";
import { ScrollText, BookOpen, CheckCircle2, ShieldCheck, Heart, AlertTriangle } from "lucide-react";
import { ResponseActions } from "@/components/Dashboard/utils/toolUtils";
import ScriptureReferencesSection from "@/components/MoralDilemmas/ScriptureReferencesSection";

interface ScriptureReference {
  reference: string;
  text: string;
  explanation?: string;
}

interface MoralDilemmasResponse {
  directAnswer: string;
  biblicalFoundation: string;
  scriptureReferences: ScriptureReference[];
  culturalContrast: string;
  personalEncouragement: string;
  callToFaithfulness: string;
  practicalSteps: string[];
}

export const renderMoralDilemmasContent = (
  item: any,
  navigate: NavigateFunction
) => {
  const { content } = item;
  if (!content || !content.response) {
    return <p>No content available</p>;
  }

  const question = content.question || "Moral dilemma question";
  const response: MoralDilemmasResponse = content.response;
  
  // Log the response for debugging
  console.log("Dashboard renderer received response:", response);
  
  // Ensure scriptureReferences is always an array of valid objects
  if (!response.scriptureReferences || !Array.isArray(response.scriptureReferences)) {
    console.log("Response missing scriptureReferences array in dashboard, creating empty array");
    response.scriptureReferences = [];
  }

  return (
    <div className="space-y-4">
      {/* Add ResponseActions at the top */}
      <ResponseActions 
        question={question} 
        response={response} 
        toolType="moral_dilemmas"
      />

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Question</h3>
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="pt-4">
            <p className="text-gray-700">{question}</p>
          </CardContent>
        </Card>
      </div>

      {/* Stack cards vertically (one on top of another) */}
      <div className="space-y-4">
        <Card className="border-heaven-gold/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-heaven-gold" />
              <span className="text-heaven-gold">Direct Answer</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <p className="text-[#403E43]">{response.directAnswer}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-heaven-gold/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-heaven-gold" />
              <span className="text-heaven-gold">Biblical Foundation</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <p className="text-[#403E43]">{response.biblicalFoundation}</p>
            </div>
          </CardContent>
        </Card>

        {/* Use the ScriptureReferencesSection component */}
        {response.scriptureReferences && response.scriptureReferences.length > 0 && (
          <ScriptureReferencesSection scriptureReferences={response.scriptureReferences} />
        )}

        <Card className="border-heaven-gold/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-heaven-gold" />
              <span className="text-heaven-gold">Cultural Contrast</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <p className="text-[#403E43]">{response.culturalContrast}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-heaven-gold/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-heaven-gold" />
              <span className="text-heaven-gold">Personal Encouragement</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <p className="text-[#403E43]">{response.personalEncouragement}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-heaven-gold/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-heaven-gold" />
              <span className="text-heaven-gold">Call to Faithfulness</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <p className="text-[#403E43]">{response.callToFaithfulness}</p>
            </div>
          </CardContent>
        </Card>

        {response.practicalSteps && response.practicalSteps.length > 0 && (
          <Card className="border-heaven-gold/10">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-heaven-gold" />
                <span className="text-heaven-gold">Practical Steps</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {response.practicalSteps.map((step, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-heaven-gold mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{step}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
